import React, { useState } from 'react';
import { Trash2, Plus, Droplets, Package, Clock, Award, Layers, Box } from 'lucide-react';

const CORE_SPECS = [
  { key: 'moisture', label: 'Moisture', icon: Droplets, placeholder: 'e.g. Max 10%' },
  { key: 'moq', label: 'MOQ (Min Order)', icon: Package, placeholder: 'e.g. 1 MT' },
  { key: 'shelf_life', label: 'Shelf Life', icon: Clock, placeholder: 'e.g. 18 months' },
  { key: 'certification', label: 'Certification', icon: Award, placeholder: 'e.g. FSSAI, APEDA, Lab Tested' },
  { key: 'grade', label: 'Grade', icon: Layers, placeholder: 'e.g. Premium / Export Quality' },
  { key: 'form', label: 'Available Forms', icon: Box, placeholder: 'e.g. Fine Powder, Coarse Powder' },
  { key: 'packaging', label: 'Packaging', icon: Package, placeholder: 'e.g. 25kg PP Bags, Custom' },
];
const CORE_KEYS = CORE_SPECS.map(s => s.key);
const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold';

const asText = (value) => (Array.isArray(value) ? value.join(', ') : (value || ''));

function CoreSpecs({ specs, onUpdate }) {
  return (
    <div>
      <label className={`${LABEL_CLS} mb-3 block`}>Key Specifications</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CORE_SPECS.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="flex items-center gap-2 bg-cream/40 rounded-lg px-3 py-2.5 border border-earth/8" data-testid={`spec-${key}`}>
            <div className="w-7 h-7 rounded-md bg-saffron/10 flex items-center justify-center flex-shrink-0">
              <Icon size={13} className="text-saffron" />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-[9px] uppercase tracking-wider text-saffron/70 font-sans font-bold block mb-0.5">{label}</label>
              <input
                value={asText(specs[key])}
                onChange={(e) => onUpdate(key, e.target.value)}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 p-0 text-sm font-sans text-earth-dark focus:ring-0 focus:outline-none placeholder:text-earth/25"
                data-testid={`spec-input-${key}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExtraSpecs({ specs, onUpdate, onRemove }) {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const extra = Object.entries(specs).filter(([key]) => !CORE_KEYS.includes(key));

  const add = () => {
    if (!newKey.trim()) return;
    onUpdate(newKey.trim(), newValue);
    setNewKey('');
    setNewValue('');
  };

  return (
    <div>
      <label className={`${LABEL_CLS} mb-2 block`}>Additional Specifications</label>
      <div className="space-y-2">
        {extra.map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xs text-saffron font-sans font-semibold w-28 flex-shrink-0 uppercase">{key.replace(/_/g, ' ')}</span>
            <input value={asText(value)} onChange={(e) => onUpdate(key, e.target.value)} className="flex-1 border border-earth/10 rounded px-2.5 py-1.5 text-xs font-sans text-earth-dark focus:ring-1 focus:ring-saffron/30 focus:outline-none bg-cream/30" data-testid={`extra-spec-input-${key}`} />
            <button onClick={() => onRemove(key)} className="text-chilli/40 hover:text-chilli transition-colors" data-testid={`remove-spec-${key}`}><Trash2 size={12} /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2 border-t border-earth/5">
          <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="Spec name" className="w-28 border border-earth/10 rounded px-2.5 py-1.5 text-xs font-sans focus:outline-none bg-cream/30" data-testid="new-spec-key" />
          <input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Value" className="flex-1 border border-earth/10 rounded px-2.5 py-1.5 text-xs font-sans focus:outline-none bg-cream/30" data-testid="new-spec-value" />
          <button onClick={add} className="text-saffron hover:text-saffron-dark transition-colors" data-testid="add-spec-btn"><Plus size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function ProductSpecs({ specs, onChange }) {
  const update = (key, value) => onChange({ ...specs, [key]: value });
  const remove = (key) => {
    const next = { ...specs };
    delete next[key];
    onChange(next);
  };
  return (
    <>
      <CoreSpecs specs={specs} onUpdate={update} />
      <ExtraSpecs specs={specs} onUpdate={update} onRemove={remove} />
    </>
  );
}
